import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../components/PageContainer";
import MainContent from "../components/MainContent";
import PageTitle from "../components/Common/PageTitle/PageTitle";
import { useFinder } from "../DataStore/FinderContext";
import Notification from "../components/Common/Notification";
import ActivityLoader from "../components/Common/ActivityLoader";
import functions from "../util/functions";
import { useTheme } from "../DataStore/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useFeesProfile } from "../DataStore/FeesProfileContext";
import { useVendor } from "../DataStore/VendorContext";
import { useAccountReport } from "../DataStore/AccountReportContext";
import { useSchWalletProfile } from "../DataStore/SchWalletProfileContext";

/**
 * FinderPage – Theme aligned (Indigo)
 * - keeps your business logic
 * - improves layout + classes for the theme css
 * - adds safe guards for missing arrays/objects
 */

const FinderPage = () => {
  const [showProfile, setShowProfile] = useState(null);

  const {
    searchResults,
    message,
    searchText,
    getRecentSearches,
    recentSearches,
    dispatch,
    getSuggestions,
    searchSuggestions,
    setSearchText,
    logResultClick,
  } = useFinder();

  const { setSelectedVendor, selectedVendor } = useVendor();
  const { openSettings } = useTheme();
  const { dispatch: studentDispatch, studentId } = useFeesProfile();
  const { dispatch: accountReportDispatch } = useAccountReport();
  const { dispatch: walletProfileDispatch } = useSchWalletProfile();
  const navigate = useNavigate();

  useEffect(() => {
    getRecentSearches();
    getSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedVendor && showProfile === "vendor") navigate("/vendor-profile");
  }, [selectedVendor, showProfile, navigate]);

  useEffect(() => {
    if (studentId && showProfile === "student") navigate("/school-fees-pstudent");
    if(studentId && showProfile === "wallet") navigate("/school-fees-wstudent");
  }, [studentId, showProfile, navigate]);

  

  const hasSuggestions = Array.isArray(searchSuggestions) && searchSuggestions.length > 0;

  const accountResults = useMemo(
    () => (searchResults && Array.isArray(searchResults.account_results) ? searchResults.account_results : []),
    [searchResults]
  );
  const vendorResults = useMemo(
    () => (searchResults && Array.isArray(searchResults.payee_results) ? searchResults.payee_results : []),
    [searchResults]
  );
  const ledgerResults = useMemo(
    () => (searchResults && Array.isArray(searchResults.ledger_results) ? searchResults.ledger_results : []),
    [searchResults]
  );
  const studentResults = useMemo(
    () => (searchResults && Array.isArray(searchResults.student_results) ? searchResults.student_results : []),
    [searchResults]
  );

  const hasAnyResults =
    accountResults.length + vendorResults.length + ledgerResults.length + studentResults.length > 0;

  const handleStatement = (account) => {
    accountReportDispatch({ type: "accountInfo", payload: account });
    navigate("/report-account-statement");
  };

  const handleManageAccount = (account) => {
    // openSettings("Account", "AccountSearchResult", account);
  };

  const handleRecentClick = (results) => {
    dispatch({ type: "searchResults", payload: results });
  };

  const composeSubtitle = (suggestion) => {
    switch (suggestion.result_type) {
      case "account":
        return `Account No: ${suggestion.subtitle}`;
      case "transaction":
        return `JE: ${suggestion.subtitle}`;
      case "vendor":
        return `Vendor ID: ${suggestion.subtitle}`;
      default:
        return suggestion.subtitle;
    }
  };

  const handleSuggestionClick = (item) => {
    setSearchText(item.subtitle);
  };

  const handleResultClick = async (type, result) => {
    switch (type) {
      case "Account":
        await logResultClick("account", result.account_name, result.account_number, result);
        break;

      case "Transaction":
        openSettings(type, "TransactionSearchResult", result, null, false);
        await logResultClick("transaction", result.transaction_ref, result.ledger_no, result);
        break;

      case "Vendor":
        await logResultClick("vendor", result.title, result.payee_id, result);
        break;

      case "Student":
        await logResultClick("student", "Student", result.full_name, result);
        break;

      default:
        openSettings(type, "SearchResult", result);
    }
  };

  const goToVendorProfile = (profileInfo) => {
    setSelectedVendor(profileInfo);
    setShowProfile("vendor");
  };

  const goToStudentProfile = (profileInfo) => {
    studentDispatch({ type: "studentId", payload: profileInfo.id });
    setShowProfile("student");
  };

  const goToStudentWallet = (profileInfo) =>{
    walletProfileDispatch({ type: "studentId", payload: profileInfo.id });
    setShowProfile("wallet");
  }

  // stop inner action links from triggering the parent click (nice UX)
  const stop = (e) => e.stopPropagation();

  return (
    <PageContainer>
      <MainContent>
        {/* If you added the CSS from earlier into global css, you can wrap the page with finder-wrap */}
        <div className="finder-wrap">
          <PageTitle
            title="Finder"
            description={
              searchText && searchText.length > 0
                ? `Search results for: ${searchText}`
                : "Quickly find accounts, transactions, vendors, reports, etc"
            }
            iconClass="fa-solid fa-magnifying-glass"
            sticky={true}
          />

          <div className="row">
            {/* Left */}
            <div className="col-md-8">
              <div className="card mb-3 yx-finderCard">
                <div className="card-body">
                  {/* Suggestions */}
                  {hasSuggestions ? (
                    <>
                      <h6 className="mb-3">Search suggestions</h6>

                      <div className="search-suggestions">
                        {searchSuggestions.map((item, key) => (
                          <div
                            key={key}
                            className="search-entry search-suggestion d-flex align-items-center"
                            onClick={() => handleSuggestionClick(item)}
                            role="button"
                            tabIndex={0}
                          >
                            <i className="fa fa-search text-muted mr-2" />
                            <div style={{ minWidth: 0 }}>
                              <h6 className="mb-0">{item.title}</h6>
                              <small className="text-muted">{composeSubtitle(item)}</small>
                            </div>
                          </div>
                        ))}
                      </div>

                      <hr />
                    </>
                  ) : null}

                  {/* Recommended */}
                  <h6 className="mb-3">Recommended for you</h6>

                  <div className="row overflow-auto" style={{ maxHeight: "550px" }}>
                    {/* Accounts */}
                    {accountResults.length > 0 ? (
                      <div className="col-md-6">
                        <div className="yx-recTitle">
                          <i className="fa fa-university" /> Accounts
                        </div>

                        {accountResults.map((account) => (
                          <div
                            key={account.account_id}
                            className="yx-result search-entry mb-3"
                            onClick={() => handleResultClick("Account", account)}
                          >
                            <div style={{ minWidth: 0 }}>
                              <h6 className="mb-1">
                                {account.account_name} ({account.account_number}) - {account.account_type}
                              </h6>
                              <small className="text-muted">{account.description}</small>

                              <div className="mt-2">
                                <span className="link fs-12" onClick={(e) => { stop(e); handleStatement(account); }}>
                                  Account Statement
                                </span>
                                <span className="ml-3 link fs-12" onClick={(e) => { stop(e); handleManageAccount(account); }}>
                                  Manage
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {/* Vendors */}
                    {vendorResults.length > 0 ? (
                      <div className="col-md-6">
                        <div className="yx-recTitle">
                          <i className="fa fa-truck" /> Vendors
                        </div>

                        {vendorResults.map((vendor) => (
                          <div
                            key={vendor.payee_id}
                            className="yx-result search-entry mb-3"
                            onClick={() => handleResultClick("Vendor", vendor)}
                          >
                            <div style={{ minWidth: 0 }}>
                              <h6 className="mb-1">{vendor.title}</h6>
                              <small className="text-muted">{vendor.description}</small>
                              <br />
                              <small className="text-muted">Contact: {vendor.phone_number}</small>

                              <div className="mt-2">
                                <span className="link fs-12" onClick={(e) => { stop(e); goToVendorProfile(vendor); }}>
                                  View Profile
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {/* Transactions */}
                    {ledgerResults.length > 0 ? (
                      <div className="col-md-6 mt-4">
                        <div className="yx-recTitle">
                          <i className="fa fa-exchange-alt" /> Transactions
                        </div>

                        {ledgerResults.map((transaction) => (
                          <div
                            key={transaction.transaction_id}
                            className="yx-result search-entry mb-3"
                            onClick={() => handleResultClick("Transaction", transaction)}
                          >
                            <div style={{ minWidth: 0 }}>
                              <h6 className="mb-1">JE: {transaction.ledger_no}</h6>

                              <small className="text-muted">
                                {transaction.description} | Amount: {functions.numberFormat(transaction.amount)} | Trans.
                                Date: {functions.formatDate(transaction.transaction_date)}
                              </small>

                              <p className="mb-1">
                                Dr: {transaction.account?.account_name} | Cr: {transaction.ledger_grouping?.account?.account_name}
                              </p>

                              <small className="text-muted">
                                Recorded By: {transaction.created_by?.username} | Payee: {transaction.payee?.title}
                              </small>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {/* Students */}
                    {studentResults.length > 0 ? (
                      <div className="col-md-6">
                        <div className="yx-recTitle">
                          <i className="fa fa-handshake" /> Students
                        </div>

                        {studentResults.map((student) => (
                          <div
                            key={student.id}
                            className="yx-result search-entry mb-3"
                            onClick={() => handleResultClick("Student", student)}
                          >
                            <div style={{ minWidth: 0 }}>
                              <h6 className="mb-1">{student.full_name}</h6>
                              <small className="text-muted">
                                {student.class?.class_name} - {student.status_name} - {student.residence?.residence_name}
                              </small>

                              <br />

                              {student.fees_group ? (
                                <>
                                  <small className="text-muted">{student.fees_group.group_name}</small>{" "}
                                  {Number(student.current_bill) > 0 ? (
                                    <i className="fa fa-circle-check text-success" />
                                  ) : null}
                                  <br />
                                </>
                              ) : null}

                              <small className="text-muted">Reg No: {student.reg_no}</small>

                              <div className="mt-2">
                                <span className="link fs-12" onClick={(e) => { stop(e); goToStudentProfile(student); }}>
                                  View Profile
                                </span>
                                 <span className="link fs-12 ml-3" onClick={(e) => { stop(e); goToStudentWallet(student); }}>
                                  Student Wallet
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {/* Empty / loading states */}
                    {!searchResults && <ActivityLoader />}

                    {searchResults === "404" && (<div className="col-12">
                      <Notification type="info" message={message} closable={false} />
                    </div>
                    )}

                    {searchResults && searchResults !== "404" && !hasAnyResults ? (
                      <Notification type="light" message="No recommendations found for this search." closable={false} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Right */}
            <div className="col-md-4">
              <div className="card mb-3 yx-finderCard">
                <div className="card-header">
                  <div>
                    <h6 className="menu-header-title card-titl" style={{ textTransform: "none", fontWeight: "normal", fontSize: "16px" }}>
                      Recent Searches
                    </h6>
                  </div>
                </div>

                <div className="card-body overflow-auto" style={{ maxHeight: "550px" }}>
                  {Array.isArray(recentSearches) &&
                    recentSearches.map((item) => (
                      <div
                        key={item.id}
                        className="yx-recentItem search-entry d-flex align-items-center mb-3"
                        onClick={() => handleRecentClick(item.results)}
                      >
                        <i className="fa-solid fa-clock-rotate-left text-muted mr-3" />
                        <div style={{ minWidth: 0 }}>
                          <h6 className="mb-0">Filter: {item.keyword}</h6>
                          <small className="text-muted">{functions.formatDate(item.date)}</small>
                        </div>
                      </div>
                    ))}

                  {recentSearches === "404" && (
                    <Notification type="light" message="No recent searches found" closable={false} />
                  )}

                  {!recentSearches && <ActivityLoader />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default FinderPage;
